import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";

function daysLeft(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Faixa de aviso de trial. Aparece apenas para admins de tenants que ainda
 * estão no período de avaliação (plan === "trial"). Mostra os dias restantes
 * e leva para a página de cobrança para assinar.
 */
export function TrialBadge() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api
      .get("/billing/plan")
      .then(({ data }) => setState(data))
      .catch(() => {});
  }, [user?.role]);

  if (!state || state.plan_id !== "trial" || !state.trial_ends_at) return null;

  const left = daysLeft(state.trial_ends_at);
  if (left === null) return null;

  const expired = left <= 0;
  const urgent = left <= 3;

  const tone = expired
    ? "from-rose-600 to-red-700 border-red-400/40"
    : urgent
      ? "from-amber-500 to-orange-600 border-amber-300/40"
      : "from-blue-600 to-indigo-700 border-blue-400/40";

  const label = expired
    ? t("Sua avaliação gratuita expirou")
    : left === 1
      ? t("Último dia da sua avaliação gratuita")
      : t("Avaliação grátis: {n} dias restantes", { n: left });

  return (
    <Link
      to="/billing"
      data-testid="trial-badge"
      className={`group mb-4 flex items-center gap-3 rounded-xl border bg-gradient-to-r ${tone} px-4 py-3 text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5`}
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
        {expired ? <AlertTriangle size={18} /> : <Clock size={18} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold leading-tight" data-testid="trial-badge-label">
          {label}
        </div>
        <div className="text-xs text-white/80">
          {expired
            ? t("Assine agora para reativar o acesso completo.")
            : t("Assine um plano para continuar sem interrupções.")}
        </div>
      </div>
      <span className="hidden sm:inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-slate-900 transition-transform group-hover:scale-105">
        <Sparkles size={13} /> {t("Assinar")}
      </span>
    </Link>
  );
}

export default TrialBadge;
